import { savePackageInclusion } from '~/components/Common/Forms/InclusionsAssetForm/utils';

import InclusionsService from '~/services/InclusionsService';

interface PackageInclusionSaveFields {
  asset_salesforce_id?: string;
  id_salesforce_external?: string;
  inclusion_id: string;
  lux_plus_tier?: string | null;
  name?: string;
  offer_id?: string;
  opportunity_name?: string;
  package_id: string;
  package_name?: string;
  sort_order?: number;
  style?: string | null;
  type?: string;
  vendor_salesforce_id?: string;
}

interface PackageInclusionAssignmentDraft extends PackageInclusionSaveFields {
  id?: undefined;
  offer_id: string;
  vendor_salesforce_id: string;
}

interface PersistedPackageInclusionUpdate extends PackageInclusionSaveFields {
  id: string;
  offer_id: string;
  vendor_salesforce_id: string;
}

export type PackageInclusionMutationPayload = PackageInclusionAssignmentDraft | PersistedPackageInclusionUpdate;

type PackageInclusionRecord = Record<string, unknown>;
type FetchedPackageInclusionRecord = PersistedPackageInclusionUpdate & PackageInclusionRecord;
type PackageInclusionAssignments = Map<string, Array<PackageInclusionAssignmentDraft>>;

interface SavePackageInclusionChangesParams {
  offerId: string;
  packageInclusionIdsToDelete?: Array<string>;
  packageInclusions: Array<PackageInclusionMutationPayload>;
}

function getString(value: unknown) {
  return value ? String(value) : undefined;
}

function isRecord(value: unknown): value is PackageInclusionRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getPackageInclusionRowId(packageInclusion: { id?: unknown }) {
  return getString(packageInclusion.id);
}

function getPackageInclusionAssignmentKey(packageInclusion: {
  inclusion_id?: unknown;
  package_id?: unknown;
  vendor_salesforce_id?: unknown;
}) {
  const inclusionId = getString(packageInclusion.inclusion_id);
  const vendorId = getString(packageInclusion.vendor_salesforce_id);
  const packageId = getString(packageInclusion.package_id);

  // saveInclusionAsset accepts all package assignments for one vendor inclusion at once.
  return inclusionId && vendorId && packageId ? `${vendorId}:${inclusionId}` : undefined;
}

function getPackageInclusionRelationKey(packageInclusion: { inclusion_id?: unknown; package_id?: unknown }) {
  const inclusionId = getString(packageInclusion.inclusion_id);
  const packageId = getString(packageInclusion.package_id);

  return inclusionId && packageId ? `${packageId}:${inclusionId}` : undefined;
}

function isPackageInclusionAssignmentDraft(
  packageInclusion: PackageInclusionMutationPayload,
): packageInclusion is PackageInclusionAssignmentDraft {
  return !getPackageInclusionRowId(packageInclusion);
}

function isPersistedPackageInclusionUpdate(
  packageInclusion: PackageInclusionMutationPayload,
): packageInclusion is PersistedPackageInclusionUpdate {
  return !!getPackageInclusionRowId(packageInclusion);
}

function isFetchedPackageInclusionRecord(value: unknown): value is FetchedPackageInclusionRecord {
  return isRecord(value) && !!getPackageInclusionRowId(value) && !!getPackageInclusionRelationKey(value);
}

function getPackageInclusionAssignments(packageInclusions: Array<PackageInclusionMutationPayload>) {
  return packageInclusions.reduce<PackageInclusionAssignments>((assignments, packageInclusion) => {
    if (!isPackageInclusionAssignmentDraft(packageInclusion)) {
      return assignments;
    }

    const key = getPackageInclusionAssignmentKey(packageInclusion);

    if (!key) {
      return assignments;
    }

    assignments.set(key, (assignments.get(key) ?? []).concat(packageInclusion));

    return assignments;
  }, new Map());
}

async function assignPackageInclusions(packageInclusionAssignments: PackageInclusionAssignments) {
  for (const packageInclusionsToAssign of packageInclusionAssignments.values()) {
    const [firstPackageInclusion] = packageInclusionsToAssign;
    const inclusionId = getString(firstPackageInclusion.inclusion_id);
    const vendorId = getString(firstPackageInclusion.vendor_salesforce_id);

    if (!inclusionId || !vendorId) {
      continue;
    }

    await InclusionsService.saveInclusionAsset(
      vendorId,
      {
        id: inclusionId,
        name: getString(firstPackageInclusion.name),
        is_hidden: false,
        type: getString(firstPackageInclusion.type) || 'Flash',
        vendor_salesforce_id: vendorId,
        packages: packageInclusionsToAssign.map((packageInclusion) => {
          const packageName = getString(packageInclusion.package_name);
          const opportunityName = getString(packageInclusion.opportunity_name);

          return {
            id_salesforce_external: getString(packageInclusion.package_id),
            name: packageName,
            deal_option_name: packageName,
            offer_name: opportunityName,
            offer_id_salesforce_external: getString(packageInclusion.offer_id),
            sort_order: packageInclusion.sort_order,
            style: packageInclusion.style,
            lux_plus_tier: packageInclusion.lux_plus_tier,
          };
        }),
      },
      inclusionId,
    );
  }
}

function getPackageInclusionsFromResponse(response: unknown) {
  const result = isRecord(response) ? response.result : undefined;

  if (!isRecord(result)) {
    return [];
  }

  return Object.values(result).flatMap((packageInclusions) =>
    Array.isArray(packageInclusions) ? packageInclusions.filter(isFetchedPackageInclusionRecord) : [],
  );
}

async function getAssignedPackageInclusionsToUpdate(
  offerId: string,
  packageInclusionAssignments: PackageInclusionAssignments,
) {
  const assignedPackageInclusions = Array.from(packageInclusionAssignments.values()).flat();

  if (assignedPackageInclusions.length === 0) {
    return [];
  }

  const fetchedPackageInclusions = getPackageInclusionsFromResponse(
    await InclusionsService.fetchPackageInclusions(offerId),
  );
  const fetchedPackageInclusionsByKey = fetchedPackageInclusions.reduce<
    Map<string, Array<FetchedPackageInclusionRecord>>
  >((packageInclusionsByKey, packageInclusion) => {
    const key = getPackageInclusionRelationKey(packageInclusion);

    if (key) {
      packageInclusionsByKey.set(key, (packageInclusionsByKey.get(key) ?? []).concat(packageInclusion));
    }

    return packageInclusionsByKey;
  }, new Map());
  const missingAssignedPackageInclusion = assignedPackageInclusions.find((packageInclusion) => {
    const key = getPackageInclusionRelationKey(packageInclusion);

    return !key || !fetchedPackageInclusionsByKey.has(key);
  });

  if (missingAssignedPackageInclusion) {
    throw new Error('Unable to save package inclusion metadata after assigning packages.');
  }

  return assignedPackageInclusions.flatMap((packageInclusion) => {
    const key = getPackageInclusionRelationKey(packageInclusion);
    const fetchedPackageInclusions = key ? fetchedPackageInclusionsByKey.get(key) : undefined;

    return (
      fetchedPackageInclusions?.flatMap((fetchedPackageInclusion) => {
        const rowId = getPackageInclusionRowId(fetchedPackageInclusion);

        return rowId ? [{ ...fetchedPackageInclusion, ...packageInclusion, id: rowId }] : [];
      }) ?? []
    );
  });
}

export async function savePackageInclusionChanges({
  offerId,
  packageInclusionIdsToDelete = [],
  packageInclusions,
}: SavePackageInclusionChangesParams) {
  if (packageInclusionIdsToDelete.length > 0) {
    await InclusionsService.unlinkPackagesByInclusionIds({ ids: packageInclusionIdsToDelete });
  }

  const packageInclusionAssignments = getPackageInclusionAssignments(packageInclusions);

  await assignPackageInclusions(packageInclusionAssignments);

  const packageInclusionsToUpdate = packageInclusions
    .filter(isPersistedPackageInclusionUpdate)
    .concat(await getAssignedPackageInclusionsToUpdate(offerId, packageInclusionAssignments));

  if (packageInclusionsToUpdate.length > 0) {
    return InclusionsService.updatePackageInclusion({
      package_inclusions: packageInclusionsToUpdate.map((packageInclusion) =>
        savePackageInclusion(packageInclusion, undefined),
      ),
    });
  }
}
